import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import DashboardService from '../services/DashboardService'; // Import the DashboardService
import UserService from '../services/UserService'; // Service to fetch user details

const Home = () => {
    const [dashboardData, setDashboardData] = useState(null);
    const [userData, setUserData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                // Fetch dashboard metrics and user details concurrently
                const [dashboard, user] = await Promise.all([
                    DashboardService.getMetrics(),
                    UserService.getMe(),
                ]);
                setDashboardData(dashboard);
                setUserData(user);
            } catch (err) {
                console.error('Error loading dashboard data:', err);
                setError('Failed to load dashboard data.');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    function formatBytes(bytes) {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        const converted = parseFloat((bytes / Math.pow(k, i)).toFixed(2));
        return `${converted} ${sizes[i]}`;
    }
    

    if (loading) {
        return <div className="content-wrapper"><h2>Loading dashboard...</h2></div>;
    }

    if (error) {
        return <div className="content-wrapper"><div className="alert alert-danger">{error}</div></div>;
    }

    return (
        <div className="content-wrapper">
            {/* Welcome Section */}
            <div className="row">
                <div className="col-md-12">
                    <h3 className="mb-4">
                        Welcome, {userData?.name || 'User'}!
                    </h3>
                    <p className="lead text-muted">
                        Here's a quick overview of your system metrics.
                    </p>
                </div>
            </div>

            {/* Metrics Section */}
            <div className="row">
                {/* Total Backups */}
                <div className="col-md-6">
                    <div className="card shadow-sm">
                        <div className="card-body d-flex align-items-center">
                            <i className="ti-harddrives menu-icon text-primary mr-3" style={{ fontSize: '2rem' }}></i>
                            <div>
                                <h3>{dashboardData.totalBackups}</h3>
                                <p>Total Backups</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Total Source Databases */}
                <div className="col-md-6">
                    <div className="card shadow-sm">
                        <div className="card-body d-flex align-items-center">
                            <i className="ti-server menu-icon text-success mr-3" style={{ fontSize: '2rem' }}></i>
                            <div>
                                <h3>{dashboardData.totalSourceDatabases}</h3>
                                <p>Total Source Databases</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Emails and Target Connections */}
            <div className="row mt-3">
                {/* Total Configured Emails */}
                <div className="col-md-6">
                    <div className="card shadow-sm">
                        <div className="card-body d-flex align-items-center">
                            <i className="ti-email menu-icon text-danger mr-3" style={{ fontSize: '2rem' }}></i>
                            <div>
                                <h3>{dashboardData.totalEmails}</h3>
                                <p>Total Configured Emails</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Total Target Connections */}
                <div className="col-md-6">
                    <div className="card shadow-sm">
                        <div className="card-body d-flex align-items-center">
                            <i className="ti-cloud menu-icon text-warning mr-3" style={{ fontSize: '2rem' }}></i>
                            <div>
                                <h3>{dashboardData.totalTargetConnections}</h3>
                                <p>Total Target Connections</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Backup Size and Last Backup Date */}
            <div className="row mt-3">
                {/* Total Backup Size */}
                <div className="col-md-6">
                    <div className="card shadow-sm">
                        <div className="card-body d-flex align-items-center">
                            <i className="ti-bar-chart menu-icon text-info mr-3" style={{ fontSize: '2rem' }}></i>
                            <div>
                            <h3>{formatBytes(dashboardData.totalBackupSizeBytes)}</h3>
                            <p>Total Backup Size</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Last Backup Date */}
                <div className="col-md-6">
                    <div className="card shadow-sm">
                        <div className="card-body d-flex align-items-center">
                            <i className="ti-calendar menu-icon text-secondary mr-3" style={{ fontSize: '2rem' }}></i>
                            <div>
                                <h3>
                                    {dashboardData.lastBackupDate
                                        ? new Date(dashboardData.lastBackupDate).toLocaleString('en-US', {
                                            weekday: 'short', // 'Mon'
                                            year: 'numeric', // '2024'
                                            month: 'short', // 'Dec'
                                            day: 'numeric', // '11'
                                            hour: 'numeric', // '11'
                                            minute: 'numeric', // '05'
                                            second: 'numeric', // '18'
                                            hour12: true, // 'AM/PM'
                                        })
                                        : 'N/A'}
                                </h3>
                                <p>Last Backup Date</p>
                            </div>
                        </div>
                    </div>
                </div>

            </div>

            {/* Actions Section */}
            <div className="row mt-4">
                {/* View Backups Button */}
                <div className="col-md-6">
                    <div className="card">
                        <div className="card-body text-center">
                            <Link to="/backups" className="btn btn-primary btn-sm">
                                View Backups
                            </Link>
                        </div>
                    </div>
                </div>

                {/* Manage Source Databases Button */}
                <div className="col-md-6">
                    <div className="card">
                        <div className="card-body text-center">
                            <Link to="/source-databases" className="btn btn-secondary btn-sm">
                                Manage Source Databases
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Home;
